import { Link } from 'react-router-dom';
import lumadriveIcon from '../assets/LumaDrive_Icon.png';
import './Terms.css';

const sections = [
  { id: 'collection', n: '1', title: 'Data We Collect and How We Use It' },
  { id: 'camera-mic', n: '2', title: 'Camera and Microphone' },
  { id: 'location', n: '3', title: 'Location (GPS)' },
  { id: 'motion', n: '4', title: 'Motion & Fitness (Accelerometer)' },
  { id: 'iap', n: '5', title: 'In-App Purchases' },
  { id: 'storage', n: '6', title: 'Data Storage and Security' },
  { id: 'sharing', n: '7', title: 'Data Sharing and Third Parties' },
  { id: 'children', n: '8', title: "Children's Privacy" },
  { id: 'retention', n: '9', title: 'Data Retention and Deletion' },
  { id: 'rights', n: '10', title: 'Your Rights' },
  { id: 'changes', n: '11', title: 'Changes to This Privacy Policy' },
  { id: 'contact', n: '12', title: 'Contact' },
];

export default function Privacy() {
  return (
    <div className="legal-page">
      <header className="legal-nav">
        <Link to="/" className="legal-brand">
          <img src={lumadriveIcon} alt="" />
          <span>LumaDrive</span>
        </Link>
        <Link to="/" className="legal-back">
          ← Back to site
        </Link>
      </header>

      <main className="legal-wrap">
        <aside className="legal-toc" aria-label="Table of contents">
          <div className="legal-toc-inner">
            <span className="legal-toc-label">Contents</span>
            <ol>
              <li>
                <a href="#overview">
                  <span className="legal-toc-num">·</span>
                  <span>Overview</span>
                </a>
              </li>
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>
                    <span className="legal-toc-num">{s.n}</span>
                    <span>{s.title}</span>
                  </a>
                </li>
              ))}
            </ol>
            <Link to="/terms" className="legal-toc-cross">
              View Terms of Use →
            </Link>
          </div>
        </aside>

        <article className="legal-doc">
          <div className="legal-hero">
            <span className="eyebrow">Legal</span>
            <h1>Privacy Policy</h1>
            <p className="legal-effective">
              <strong>Effective:</strong> May 12, 2026
            </p>
          </div>

          <section id="overview">
            <h2>
              <span className="legal-h-num">·</span>Overview
            </h2>
            <p>
              LumaDrive ("the App") is a dashcam application for iPhone. This Privacy Policy
              explains what data the App accesses, how it is used, where it is stored, and your
              rights regarding that data.
            </p>
            <div className="legal-callout legal-callout-info">
              <strong>Short version:</strong> LumaDrive stores everything on your device. Nothing
              is collected, transmitted, or shared with the developer or any third party.
            </div>
          </section>

          <section id="collection">
            <h2>
              <span className="legal-h-num">1</span>Data We Collect and How We Use It
            </h2>
            <div className="legal-table-wrap">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Data type</th>
                    <th>Purpose</th>
                    <th>Stored</th>
                    <th>Transmitted</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Camera video</td>
                    <td>Record driving footage</td>
                    <td>On-device only</td>
                    <td>Never</td>
                  </tr>
                  <tr>
                    <td>Microphone audio</td>
                    <td>Embed audio into clips</td>
                    <td>On-device only</td>
                    <td>Never</td>
                  </tr>
                  <tr>
                    <td>GPS / location</td>
                    <td>Embed speed, heading, coordinates into clips</td>
                    <td>On-device only</td>
                    <td>Never</td>
                  </tr>
                  <tr>
                    <td>Accelerometer</td>
                    <td>Detect sudden impacts (incident detection)</td>
                    <td>On-device only</td>
                    <td>Never</td>
                  </tr>
                  <tr>
                    <td>Purchase receipts</td>
                    <td>Verify LumaDrive Premium subscription / lifetime unlock</td>
                    <td>Apple servers (by Apple, not us)</td>
                    <td>Handled by Apple</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              No analytics SDKs, crash reporters, advertising networks, or tracking libraries are
              included in the App.
            </p>
          </section>

          <section id="camera-mic">
            <h2>
              <span className="legal-h-num">2</span>Camera and Microphone
            </h2>
            <p>
              The App requests access to your device's camera and microphone solely to record
              driving footage. Video and audio are written directly to your device's local storage
              (the app's Documents directory). They are never uploaded, streamed, or transmitted
              anywhere.
            </p>
          </section>

          <section id="location">
            <h2>
              <span className="legal-h-num">3</span>Location (GPS)
            </h2>
            <p>
              The App requests "When In Use" location access to read your current GPS coordinates,
              speed, and heading. This data is used exclusively to embed driving telemetry into
              video clip files. Location data is stored only inside the video clips on your device
              and is never transmitted to the developer or any third party.
            </p>
            <p>
              You may revoke location permission at any time in iOS Settings → Privacy &amp;
              Security → Location Services → LumaDrive. The App will continue to function without
              location access; clips will simply not contain GPS telemetry.
            </p>
          </section>

          <section id="motion">
            <h2>
              <span className="legal-h-num">4</span>Motion &amp; Fitness (Accelerometer)
            </h2>
            <p>
              The App reads accelerometer data to power the incident detection (Beta) feature,
              which automatically locks a clip when a significant impact is detected. This sensor
              data is processed in real time on your device and is not stored, logged, or
              transmitted.
            </p>
          </section>

          <section id="iap">
            <h2>
              <span className="legal-h-num">5</span>In-App Purchases
            </h2>
            <p>LumaDrive offers optional premium features via:</p>
            <ul className="legal-list">
              <li>
                <strong>Monthly Subscription</strong> (USD $3.99/month, auto-renewing)
              </li>
              <li>
                <strong>Lifetime Unlock</strong> (USD $29.99, one-time non-consumable)
              </li>
            </ul>
            <p>
              All purchase transactions are processed entirely by Apple through the App Store. The
              developer does not receive or store your payment information, Apple ID, or any
              billing details. Purchase verification is handled by Apple's StoreKit framework
              running locally on your device.
            </p>
          </section>

          <section id="storage">
            <h2>
              <span className="legal-h-num">6</span>Data Storage and Security
            </h2>
            <p>
              All recordings, GPS telemetry, and clip metadata are stored exclusively in the App's
              local Documents directory on your device. The developer has no access to this data
              and cannot retrieve it under any circumstances.
            </p>
            <p>
              You are responsible for the security of your device and any footage stored on it.
              The developer cannot recover footage that has been deleted, corrupted, or lost for
              any reason.
            </p>
          </section>

          <section id="sharing">
            <h2>
              <span className="legal-h-num">7</span>Data Sharing and Third Parties
            </h2>
            <p>
              The App does not share any data with third parties. There are no advertising
              networks, analytics providers, data brokers, or social media integrations.
            </p>
            <p>
              The only external service the App communicates with is the Apple App Store for
              purchase verification — this communication is handled entirely by Apple and is
              subject to Apple's own Privacy Policy.
            </p>
          </section>

          <section id="children">
            <h2>
              <span className="legal-h-num">8</span>Children's Privacy
            </h2>
            <p>
              LumaDrive is not directed at children under the age of 13 and does not knowingly
              collect information from children. If you believe a child has provided personal
              information through the App, please contact us so we can address the situation.
            </p>
          </section>

          <section id="retention">
            <h2>
              <span className="legal-h-num">9</span>Data Retention and Deletion
            </h2>
            <p>
              The App stores clips on your device until you delete them manually. There is no
              automatic deletion by the developer because the developer has no access to your
              data.
            </p>
            <p>To delete all App data:</p>
            <ol className="legal-list legal-list-ordered">
              <li>Delete individual clips within the App, or</li>
              <li>
                Delete the App from your device (this removes all clips stored in the App's
                Documents directory).
              </li>
            </ol>
          </section>

          <section id="rights">
            <h2>
              <span className="legal-h-num">10</span>Your Rights
            </h2>
            <p>
              Because LumaDrive does not collect or transmit personal data to the developer, there
              is no personal data held by the developer that can be accessed, corrected, or
              deleted upon request. All data remains under your sole control on your device.
            </p>
            <p>
              For users in the European Economic Area (EEA), United Kingdom, or other
              jurisdictions with data protection laws: the App processes location and sensor data
              locally on-device as a technical necessity to provide the dashcam function. No
              personal data is transferred outside your device to the developer.
            </p>
          </section>

          <section id="changes">
            <h2>
              <span className="legal-h-num">11</span>Changes to This Privacy Policy
            </h2>
            <p>
              The developer may update this Privacy Policy from time to time. Material changes
              will be communicated within the App upon next launch. Continued use of the App after
              such notice constitutes acceptance of the revised policy.
            </p>
          </section>

          <section id="contact">
            <h2>
              <span className="legal-h-num">12</span>Contact
            </h2>
            <p>For privacy-related questions or concerns, contact:</p>
            <p>
              <a className="legal-mail" href="mailto:mukuwabaffoe@gmail.com">
                mukuwabaffoe@gmail.com
              </a>
            </p>
          </section>

          <div className="legal-footer">
            <Link to="/terms" className="legal-back-bottom">
              View Terms of Use →
            </Link>
            <Link to="/" className="legal-back-bottom">
              ← Back to LumaDrive
            </Link>
          </div>
        </article>
      </main>
    </div>
  );
}
